import { useEffect, useState } from 'react';

/**
 * useBreakpoint — single shared responsive hook for the calendar.
 *
 * Replaces six divergent `useIsNarrow` copies (DayCell, CreateEventModal,
 * EventViewModal, CalendarFilterBar, SavedFiltersDropdown, CalendarMonthNav)
 * that hard-coded the cutoffs 639 / 767 / 1023 inline. This is the single
 * source of truth for calendar responsive behavior (Month V2 today;
 * Week/Resource/Dispatch inherit it later).
 *
 * Content-based breakpoints (Refactoring UI). Cutoffs mirror the legacy
 * `useIsNarrow` values exactly so the migration is behavior-preserving:
 *   - phone      < 640  (legacy `useIsNarrow()`        — Tailwind `sm`)
 *   - belowTablet < 768 (legacy `useIsNarrow(767)`     — Tailwind `md`)
 *   - navCompact < 1024 (legacy `useIsNarrow(1023)`    — Tailwind `lg`)
 */

// Breakpoint cutoffs as `max-width` px — single source of truth.
export const BREAKPOINTS = {
	phone: 639, // < 640
	belowTablet: 767, // < 768
	navCompact: 1023 // < 1024
};

/**
 * matchMedia-based boolean for `(max-width: ${maxPx}px)`.
 *
 * SSR-safe: returns `false` until mounted (matches the original `useIsNarrow`
 * default state). Subscribes to the `change` event and cleans up on unmount —
 * mirrors the canonical pattern from EventViewModal.js:280-291.
 */
function useMaxWidth(maxPx) {
	const [matches, setMatches] = useState(false);
	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return undefined;
		const mq = window.matchMedia(`(max-width: ${maxPx}px)`);
		const apply = () => setMatches(mq.matches);
		apply();
		mq.addEventListener?.('change', apply);
		return () => mq.removeEventListener?.('change', apply);
	}, [maxPx]);
	return matches;
}

/**
 * Returns the active responsive tokens for the current viewport.
 *
 * Named tokens (calendar-wide contract):
 *   - `phone`          viewport < 640
 *   - `tablet`         viewport 640–767
 *   - `navCompact`     viewport < 1024
 *   - `desktopSidebar` viewport >= 1024
 *
 * Derived helper (kept because the filter bar / saved-filters dropdown need
 * the `< 768` cutoff directly):
 *   - `belowTablet`    viewport < 768
 */
export default function useBreakpoint() {
	const phone = useMaxWidth(BREAKPOINTS.phone); // < 640
	const belowTablet = useMaxWidth(BREAKPOINTS.belowTablet); // < 768
	const navCompact = useMaxWidth(BREAKPOINTS.navCompact); // < 1024

	return {
		phone, // < 640
		tablet: belowTablet && !phone, // 640–767
		navCompact, // < 1024
		desktopSidebar: !navCompact, // >= 1024
		belowTablet // < 768
	};
}
